import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { SidebarIdContent } from '@jira/navigation-apps__constants/src/SidebarIdContent.tsx';
import { aiEntitlementResource } from '@jira/router-resources__ai-entitlement/src/aiEntitlementResource.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
	getUIFBacklogDataResource,
} from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { getEditModelResource } from '@jira/router-resources__classic-projects/src/getEditModelResource.tsx';
import { getBoardDetailsResource } from '@jira/router-resources__classic-projects/src/getBoardDetailsResource.tsx';
import { backlogCMPViewSettingsResource } from '@jira/router-resources__classic-projects-uif-backlog/src/uifBacklogResource.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { classicSoftwareBacklogRoute } from '@jira/router-routes__software-classic-backlog-routes/src/classicSoftwareBacklogRoute.tsx';
import { NextGenBacklogSkeleton } from '@jira/platform__skeletons/src/ui/next-gen-backlog/NextGenBacklogSkeleton.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { pageLoadIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import {
	forgeBacklogModuleResource,
	forgeSprintModuleResource,
} from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';

import { epicsLiteResource } from '@jira/router-resources__next-gen-backlog/src/epicsLiteResource.tsx';
import { permissionsResource } from '@jira/router-resources__next-gen-backlog/src/permissionsResource.tsx';
import { confluenceUserPermissionsResource } from '@jira/router-resources__confluence-user-permissions/src/confluenceUserPermissionsResource.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { AGGRESSIVE_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import {
	classicBacklogPageEntryPoint,
	GSNClassicBacklogPageEntryPoint,
} from './ui/page-entry-point/index.tsx';
import { classicBacklogCapability } from './common/constants.tsx';
import { LazyBacklogPage } from './ui/LazyBacklogPage.tsx';
import type { Route } from '@atlassian/react-resource-router';

/*
 * Inline the early script in the payload only if SSR is skipped.
 */
const getEarlyEntry = (): string[] => {
	// ensure code is removed from client-side bundle
	if (__SERVER__) {
		// Legacy SSR does not support reading cookies and will be gone soon
		// this ensures jira-routes.js and static manifest does not change behaviour.
		return __SERVER__ && fg('enable_ssr_for_cmp_backlog')
			? []
			: ['software-cmp-backlog-early-entry'];
	}
	return ['software-cmp-backlog-early-entry'];
};

export const classicSoftwareBacklogRouteEntry: Route = createEntry(
	classicSoftwareBacklogRoute,
	() => ({
		group: 'classic-software',
		ufoName: 'classic-backlog',
		skeleton: NextGenBacklogSkeleton,
		layout: composeLayouts(
			genericProjectLayout,
			pageLoadIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }], 'SIDEBAR'),
		),
		navigation: {
			menuId: MenuIdProjects,
			horizontal: AsyncHorizontalNavJira,
			sidebarId: SidebarIdContent,
		},
		resources: [
			...getNavigationResources({ isUsingSidebarEntrypoint: true }),
			...getIssueViewResources(),
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
			// end of resources expanded from getNavigationSidebarProjectResource
			...getUIFBacklogDataResource(),
			...getRapidboardResources(),
			getRapidBoardDataResource(),
			getEditModelResource(),
			getBoardDetailsResource(),
			forgeBacklogModuleResource,
			forgeSprintModuleResource,
			projectThemeResource,
			aiEntitlementResource,
			resourceWithCondition(
				() => !__SERVER__ || fg('enable_ssr_for_cmp_backlog'),
				epicsLiteResource,
			),
			backlogCMPViewSettingsResource,
			currentUserResource,
			permissionsResource,
			resourceWithCondition(
				() => fg('twcg-992-loom-meeting-notes-sprint-planning-gate'),
				confluenceUserPermissionsResource,
			),
		],
		afterPaint: [LazyIssueApp],
		forPaint: [LazyAtlassianNavigation, LazyBacklogPage],
		preloadOptions: {
			earlyChunks: getEarlyEntry(),
		},
		meta: {
			capability: classicBacklogCapability,
		},
		earlyAssets: AGGRESSIVE_EARLY_ASSET_STRATEGY,
		bifrost: {
			bifrostGroup: 'backlog-and-board',
			manifestName: 'jira-spa-software-backlog.json',
			fetchTimeout: expVal('increase_ssr_fetching_timeout_for_classic_backlog', 'isEnabled', false)
				? 1500
				: undefined,
		},
		entryPoint: () =>
			fg('navx-global-side-nav') ? GSNClassicBacklogPageEntryPoint : classicBacklogPageEntryPoint,
		entryPointOptions: { isSSRPlaceholderEnabled: true },
		supportsPreviewPanel: true,
		supportsProjectTheme: 'full',
		isHydrateRootEnabled: fg('cmp-backlog-hydrate-root'),
		isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-backlog'),
		excludeFromWrmDeferredLoading: true,
	}),
);
